import { useState, useEffect, useRef, useCallback } from 'react';
import { X, PiggyBank } from 'lucide-react';
import Button from './Button.jsx';
import CategoryCombobox from './CategoryCombobox.jsx';
import { useCreateDebt, useUpdateDebt } from '../hooks/useDebts.js';
import { useCreateProvision } from '../hooks/useProvisions.js';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import { formatCurrency } from '../lib/format.js';
import {
  addMonthsISO,
  monthsBetween,
  solveAnnualRateClient,
  computeMonthlyPaymentCents,
  computeDrawdownProvisionClient,
  computeEndDate,
  INTEREST_MODE_LABELS,
  PAYMENT_FREQUENCY_LABELS,
} from '../lib/debtHelpers.js';

const SOURCE_TYPES = [
  { value: 'bank_loan',          label: 'Prêt bancaire' },
  { value: 'pge',                label: 'PGE' },
  { value: 'shareholder_advance', label: 'Apport associé' },
  { value: 'private_investor',   label: 'Investisseur privé' },
  { value: 'supplier_credit',    label: 'Crédit fournisseur' },
  { value: 'other',              label: 'Autre' },
];

const REPAYMENT_MODES = [
  { value: 'linear',             label: 'Linéaire' },
  { value: 'in_fine',            label: 'In fine' },
  { value: 'sales_proportional', label: 'Proportionnel au CA' },
];

function emptyForm() {
  return {
    name: '',
    source_type: 'bank_loan',
    capital: '',
    annual_rate_pct: '',
    duration_months: '',  // dérivé de (start_date, end_date, grace) — non éditable
    start_date: '',
    end_date: '',
    grace_period_months: '0',
    repayment_mode: 'linear',
    sales_pct: '',
    monthly_ca_estimate: '',
    category_id: '',
    counterparty_hint: '',
    notes: '',
    monthly_payment: '',
    input_mode: 'rate',
    // Périodicité
    payment_frequency_months: '1',
    first_payment_date: '',
    // Comptes bancaires + provision
    funding_bank_account_id: '',
    provision_scope: 'none',
    // Revolving
    is_revolving: false,
    planned_repayment_date: '',
    interest_mode: 'capitalized',
    provision_bank_account_id: '',
  };
}

function formFromDebt(debt) {
  if (!debt) return emptyForm();
  const startDate = debt.start_date ?? '';
  const dur = debt.duration_months != null ? Number(debt.duration_months) : NaN;
  // end_date = date du dernier paiement — même axe que le backend (cf. computeEndDate)
  const endDate = !isNaN(dur) && dur > 0 ? (computeEndDate(debt) ?? '') : '';
  const hasMonthlyPayment = debt.monthly_payment_cents != null;
  return {
    name:                      debt.name ?? '',
    source_type:               debt.source_type ?? 'bank_loan',
    capital:                   debt.capital_cents != null ? String(debt.capital_cents / 100) : '',
    annual_rate_pct:           debt.annual_rate_pct != null ? String(debt.annual_rate_pct) : '',
    duration_months:           !isNaN(dur) && dur > 0 ? String(dur) : '',
    start_date:                startDate,
    end_date:                  endDate,
    grace_period_months:       debt.grace_period_months != null ? String(debt.grace_period_months) : '0',
    repayment_mode:            debt.repayment_mode ?? 'linear',
    sales_pct:                 debt.sales_pct != null ? String(debt.sales_pct) : '',
    monthly_ca_estimate:       debt.monthly_ca_estimate_cents != null
                                 ? String(debt.monthly_ca_estimate_cents / 100)
                                 : '',
    category_id:               debt.category_id ?? '',
    counterparty_hint:         debt.counterparty_hint ?? '',
    notes:                     debt.notes ?? '',
    monthly_payment:           hasMonthlyPayment ? String(debt.monthly_payment_cents / 100) : '',
    input_mode:                hasMonthlyPayment ? 'monthly_payment' : 'rate',
    // Périodicité
    payment_frequency_months:  String(debt.payment_frequency_months ?? 1),
    first_payment_date:        debt.first_payment_date ?? '',
    // Comptes bancaires + provision
    funding_bank_account_id:   debt.funding_bank_account_id ?? '',
    provision_scope:           debt.provision_scope ?? 'none',
    // Revolving
    is_revolving:              debt.is_revolving ?? false,
    planned_repayment_date:    debt.planned_repayment_date ?? '',
    interest_mode:             debt.interest_mode ?? 'capitalized',
    provision_bank_account_id: debt.provision_bank_account_id ?? '',
  };
}

/**
 * Calcule la durée en mois à partir des dates, du différé et de la périodicité.
 *
 * end_date = date du dernier prélèvement. La durée reste exprimée en MOIS (c'est
 * la colonne en base), mais doit être un multiple entier de la périodicité :
 *   periods  = floor(monthsBetween(ancre, end) / freq) + 1
 *   duration = periods × freq
 *
 * L'ancre est first_payment_date si renseignée (terme échu), sinon
 * start_date + différé (terme à échoir, convention historique).
 *
 * Retourne NaN si dates manquantes ou fin antérieure à la première échéance.
 */
function computeDurationFromDates(start_date, end_date, grace_period_months, payment_frequency_months, first_payment_date) {
  if (!start_date || !end_date) return NaN;
  const grace = parseInt(grace_period_months || '0', 10) || 0;
  const freq  = parseInt(payment_frequency_months || '1', 10) || 1;

  const anchor = first_payment_date || addMonthsISO(start_date, grace);
  if (end_date < anchor) return NaN;

  const periods = Math.floor(monthsBetween(anchor, end_date) / freq) + 1;
  const dur = periods * freq;
  return dur > 0 ? dur : NaN;
}

function validateForm(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Nom requis';
  if (!form.source_type) errors.source_type = 'Source requise';
  const capital = parseFloat(form.capital);
  if (!form.capital || isNaN(capital) || capital <= 0) errors.capital = 'Capital requis (> 0)';
  if (!form.start_date) errors.start_date = 'Date de début requise';

  if (form.is_revolving) {
    // Mode enveloppe revolving : date in fine obligatoire, pas de date de fin classique
    if (!form.planned_repayment_date) {
      errors.planned_repayment_date = 'Date de remboursement in fine requise';
    } else if (form.start_date && form.planned_repayment_date <= form.start_date) {
      errors.planned_repayment_date = 'La date in fine doit être postérieure à la date de début';
    }
  } else {
    if (!form.end_date) errors.end_date = 'Date de fin requise';
    const dur = parseInt(form.duration_months, 10);
    if (form.start_date && form.end_date && (isNaN(dur) || dur <= 0)) {
      errors.end_date = 'Date de fin invalide (doit être après la première échéance)';
    }
    if (!form.repayment_mode) errors.repayment_mode = 'Mode requis';

    const freq = parseInt(form.payment_frequency_months || '1', 10) || 1;
    if (form.first_payment_date && form.start_date && form.first_payment_date < form.start_date) {
      errors.first_payment_date = 'La première échéance ne peut pas précéder la date de début';
    }
    if (freq !== 1) {
      // Reflète les CHECK de la migration 0065 — mieux vaut le dire ici qu'un 400 opaque.
      if (form.repayment_mode === 'sales_proportional') {
        errors.payment_frequency_months = 'Le mode proportionnel au CA est mensuel uniquement';
      }
      const grace = parseInt(form.grace_period_months || '0', 10) || 0;
      if (grace % freq !== 0) {
        errors.grace_period_months = `Le différé doit être un multiple de ${freq} mois`;
      }
    }

    // Cf. CHECK debt_provision_scope_needs_account (0067) : sans compte dédié,
    // le provisionné théorique n'a rien à quoi être confronté.
    if (form.provision_scope !== 'none' && !form.provision_bank_account_id) {
      errors.provision_bank_account_id = 'Compte dédié requis pour activer le provisionnement';
    }
  }

  if (form.input_mode === 'monthly_payment' && !form.is_revolving) {
    const dur = parseInt(form.duration_months, 10);
    const mp = parseFloat(form.monthly_payment);
    if (!form.monthly_payment || isNaN(mp) || mp <= 0) {
      errors.monthly_payment = 'Mensualité requise (> 0)';
    } else if (!isNaN(capital) && capital > 0 && !isNaN(dur) && dur > 0) {
      if (mp * dur < capital) {
        errors.monthly_payment = 'Mensualité trop faible pour rembourser le capital sur la durée';
      }
    }
  } else if (!form.is_revolving) {
    const rate = parseFloat(form.annual_rate_pct);
    if (form.annual_rate_pct === '' || isNaN(rate) || rate < 0) {
      errors.annual_rate_pct = 'Taux requis (>= 0)';
    }
  } else {
    // Revolving : taux toujours saisi (peut être 0)
    const rate = parseFloat(form.annual_rate_pct);
    if (form.annual_rate_pct === '' || isNaN(rate) || rate < 0) {
      errors.annual_rate_pct = 'Taux annuel requis (>= 0)';
    }
  }

  if (!form.is_revolving && form.repayment_mode === 'sales_proportional') {
    const pct = parseFloat(form.sales_pct);
    if (!form.sales_pct || isNaN(pct) || pct <= 0 || pct > 100) {
      errors.sales_pct = '% CA requis (0–100)';
    }
    const ca = parseFloat(form.monthly_ca_estimate);
    if (!form.monthly_ca_estimate || isNaN(ca) || ca <= 0) {
      errors.monthly_ca_estimate = 'CA mensuel estimé requis';
    }
  }

  return errors;
}

function validateProvisionForm(prov) {
  const errors = {};
  if (!prov.name.trim()) errors.prov_name = 'Nom de la provision requis';
  if (!prov.category_id) errors.prov_category_id = 'Catégorie requise';
  const funding = parseFloat(prov.funding_monthly);
  if (!prov.funding_monthly || isNaN(funding) || funding <= 0) {
    errors.prov_funding = 'Dotation mensuelle requise (> 0)';
  }
  if (!prov.start_date) errors.prov_start_date = 'Date de début requise';
  return errors;
}

/**
 * Calcule la date cible : start_date + duration_months → ISO yyyy-MM-dd.
 * Retourne '' si les paramètres sont invalides.
 */
function computeTargetDate(startDate, durationMonths) {
  if (!startDate || !durationMonths) return '';
  const dur = parseInt(durationMonths, 10);
  if (isNaN(dur) || dur <= 0) return '';
  const d = new Date(startDate);
  if (isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + dur);
  return d.toISOString().slice(0, 10);
}

/**
 * Calcule le total à provisionner : capital + intérêts cumulés sur la durée.
 * Formule : intérêts = capital × (taux/100) × (durée_mois/12) → upper bound conservateur
 * pour un In Fine (intérêts simples) et un linéaire (qui est ~ moitié, mais on
 * surprovisionne légèrement pour avoir de la marge).
 * Retourne null si paramètres invalides.
 */
function computeTotalToProvision(capital, durationMonths, annualRatePct) {
  const cap  = parseFloat(capital);
  const dur  = parseInt(durationMonths, 10);
  const rate = parseFloat(annualRatePct);
  if (isNaN(cap) || cap <= 0 || isNaN(dur) || dur <= 0) return null;
  const safeRate = isNaN(rate) || rate < 0 ? 0 : rate;
  const totalInterest = cap * (safeRate / 100) * (dur / 12);
  return cap + totalInterest;
}

/**
 * Calcule la dotation mensuelle : (capital + intérêts) / duration_months en euros,
 * arrondi au centime. Retourne '' si les paramètres sont invalides.
 */
function computeFundingMonthly(capital, durationMonths, annualRatePct) {
  const total = computeTotalToProvision(capital, durationMonths, annualRatePct);
  if (total === null) return '';
  const dur = parseInt(durationMonths, 10);
  return String(Math.round((total / dur) * 100) / 100);
}

/**
 * Calcule le montant cible total (capital + intérêts) en euros, arrondi au centime.
 * Retourne '' si les paramètres sont invalides.
 */
function computeTargetAmount(capital, durationMonths, annualRatePct) {
  const total = computeTotalToProvision(capital, durationMonths, annualRatePct);
  if (total === null) return '';
  return String(Math.round(total * 100) / 100);
}

function emptyProvisionForm(debtForm) {
  return {
    name: '',
    category_id: '',
    funding_monthly: computeFundingMonthly(debtForm.capital, debtForm.duration_months, debtForm.annual_rate_pct),
    target_date:     computeTargetDate(debtForm.start_date, debtForm.duration_months),
    target_amount:   computeTargetAmount(debtForm.capital, debtForm.duration_months, debtForm.annual_rate_pct),
    bank_account_id: '',
    notes: '',
    start_date:      debtForm.start_date,
  };
}

function Field({ label, children, error, required, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

// ── Section provision intégrée dans le formulaire ─────────────────────────────

function ProvisionSection({ debtForm, errors, isDisabled, linkedProvision }) {
  const [enabled, setEnabled] = useState(false);
  const [prov, setProv] = useState(() => emptyProvisionForm(debtForm));

  // Refs pour suivre si l'utilisateur a surpassé la valeur auto
  const dirtyFunding  = useRef(false);
  const dirtyDate     = useRef(false);
  const dirtyAmount   = useRef(false);

  // Expose l'état courant au parent via une ref partagée (pattern callback)
  // Le parent lit enabledRef et provRef à la soumission.
  // On utilise un attribut data-* pour exposer les valeurs hors React tree.
  // Solution plus simple : le composant parent passe une ref de callback.
  // Ici, on préfère l'approche "state lifting via callback onChange".

  // Recalcul auto quand capital / durée / taux / start_date changent dans le formulaire parent
  useEffect(() => {
    const autoFunding = computeFundingMonthly(debtForm.capital, debtForm.duration_months, debtForm.annual_rate_pct);
    const autoDate    = computeTargetDate(debtForm.start_date, debtForm.duration_months);
    const autoAmount  = computeTargetAmount(debtForm.capital, debtForm.duration_months, debtForm.annual_rate_pct);
    const autoName    = debtForm.name ? `Provision ${debtForm.name}` : '';

    setProv((prev) => ({
      ...prev,
      // Nom : recalcule seulement si vide ou valeur précédente = "Provision <nom_précédent>"
      name: prev.name === '' || prev.name === `Provision ${debtForm.name}`.trim()
        ? autoName
        : prev.name,
      funding_monthly: dirtyFunding.current ? prev.funding_monthly : autoFunding,
      target_date:     dirtyDate.current    ? prev.target_date    : autoDate,
      target_amount:   dirtyAmount.current  ? prev.target_amount  : autoAmount,
      start_date:      debtForm.start_date,
    }));
  }, [debtForm.capital, debtForm.duration_months, debtForm.annual_rate_pct, debtForm.start_date, debtForm.name]);

  function setP(field, value) {
    setProv((prev) => ({ ...prev, [field]: value }));
  }

  function handleFundingChange(v) {
    dirtyFunding.current = true;
    setP('funding_monthly', v);
  }

  function handleDateChange(v) {
    dirtyDate.current = true;
    setP('target_date', v);
  }

  function handleAmountChange(v) {
    dirtyAmount.current = true;
    setP('target_amount', v);
  }

  // Réinitialise le dirty tracking quand on coche/décoche
  function toggleEnabled() {
    if (!enabled) {
      dirtyFunding.current  = false;
      dirtyDate.current     = false;
      dirtyAmount.current   = false;
    }
    setEnabled((v) => !v);
  }

  const { data: bankAccountsData } = useBankAccounts();
  const bankAccounts = Array.isArray(bankAccountsData)
    ? bankAccountsData
    : (bankAccountsData?.accounts ?? bankAccountsData?.data ?? []);

  // En mode édition avec provision déjà liée : encart lecture seule
  if (linkedProvision) {
    const balance = linkedProvision.balance_cents ?? 0;
    const target  = linkedProvision.target_amount_cents ?? 0;
    const pct     = target > 0 ? Math.min(100, Math.round((balance / target) * 100)) : 0;

    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PiggyBank size={15} className="text-emerald-600 shrink-0" aria-hidden />
          <p className="text-sm font-semibold text-emerald-800">Provision liée</p>
        </div>
        <p className="text-sm text-emerald-700 font-medium">{linkedProvision.name}</p>

        {target > 0 && (
          <div>
            <div className="flex justify-between text-[11px] text-emerald-600 mb-1">
              <span>{formatCurrency(balance)}</span>
              <span>{pct} % · objectif {formatCurrency(target)}</span>
            </div>
            <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        <p className="text-xs text-emerald-600">
          Pour gérer cette provision (mouvements, clôture…), accède à la page détail de la dette.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      {/* Checkbox d'activation */}
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={toggleEnabled}
          disabled={isDisabled}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <PiggyBank size={15} className="text-emerald-600" aria-hidden />
          Activer une provision pour ce prêt
        </span>
      </label>

      {enabled && (
        <div className="space-y-3 pt-1 border-t border-slate-200">
          <p className="text-[11px] text-slate-400">
            Les valeurs sont pré-calculées depuis le capital et la durée. Tu peux les modifier.
          </p>

          <Field label="Nom de la provision" error={errors.prov_name} required>
            <input
              type="text"
              className={inputClass}
              value={prov.name}
              onChange={(e) => setP('name', e.target.value)}
              placeholder="Provision Prêt In Fine…"
              disabled={isDisabled}
            />
          </Field>

          <Field label="Catégorie" error={errors.prov_category_id} required>
            <CategoryCombobox
              value={prov.category_id}
              onChange={(v) => setP('category_id', v ?? '')}
              placeholder="Catégorie de provision…"
            />
          </Field>

          {(() => {
            const cap  = parseFloat(debtForm.capital);
            const dur  = parseInt(debtForm.duration_months, 10);
            const rate = parseFloat(debtForm.annual_rate_pct);
            const ok = !isNaN(cap) && cap > 0 && !isNaN(dur) && dur > 0;
            if (!ok) return null;
            const safeRate = isNaN(rate) || rate < 0 ? 0 : rate;
            const interest = cap * (safeRate / 100) * (dur / 12);
            if (interest <= 0) return null;
            return (
              <p className="text-[11px] text-slate-500 -mt-1">
                Total à provisionner = {cap.toLocaleString('fr-FR')} € de capital + {interest.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} € d'intérêts sur {dur} mois.
              </p>
            );
          })()}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Dotation mensuelle (€)" error={errors.prov_funding} required>
              <input
                type="number"
                className={inputClass}
                value={prov.funding_monthly}
                onChange={(e) => handleFundingChange(e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="416.67"
                disabled={isDisabled}
              />
            </Field>

            <Field label="Montant cible (€)">
              <input
                type="number"
                className={inputClass}
                value={prov.target_amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="10000"
                disabled={isDisabled}
              />
            </Field>
          </div>

          <Field label="Date cible">
            <input
              type="date"
              className={inputClass}
              value={prov.target_date}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={isDisabled}
            />
          </Field>

          <Field
            label="Compte bancaire associé"
            hint="Optionnel — pour suivre que les fonds sont bien mis de côté."
          >
            <select
              className={inputClass}
              value={prov.bank_account_id}
              onChange={(e) => setP('bank_account_id', e.target.value)}
              disabled={isDisabled}
            >
              <option value="">Aucun pour l'instant</option>
              {bankAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name ?? acc.label ?? acc.id}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={prov.notes}
              onChange={(e) => setP('notes', e.target.value)}
              rows={2}
              placeholder="Informations complémentaires…"
              disabled={isDisabled}
            />
          </Field>
        </div>
      )}

      {/* Expose l'état courant via dataset pour que le parent puisse le lire */}
      <input type="hidden" data-provision-enabled={String(enabled)} />
      <input type="hidden" data-provision-form={JSON.stringify(prov)} />
    </div>
  );
}

// ── Toast minimaliste ─────────────────────────────────────────────────────────

function Toast({ message, type, onDismiss }) {
  const bg = type === 'error' ? 'bg-red-600'
    : type === 'warning' ? 'bg-amber-600'
    : 'bg-slate-800';
  return (
    <div
      className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] ${bg} text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-3 lg:bottom-6 max-w-[90vw]`}
      role="status"
    >
      <span>{message}</span>
      <button
        onClick={onDismiss}
        className="text-white/60 hover:text-white ml-1 text-lg leading-none focus-visible:outline-none"
        aria-label="Fermer la notification"
      >
        ×
      </button>
    </div>
  );
}

/**
 * DebtForm — slide-over modal for creating or editing a debt instrument.
 *
 * Props:
 *   open    : boolean
 *   onClose : () => void
 *   debt    : object | undefined — if provided, edit mode
 */
export default function DebtForm({ open, onClose, debt }) {
  const isEdit = Boolean(debt);
  const createDebt = useCreateDebt();
  const updateDebt = useUpdateDebt();
  const createProvision = useCreateProvision();
  const mutation = isEdit ? updateDebt : createDebt;

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Ref vers le sous-composant ProvisionSection pour lire son état
  const provisionSectionRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(formFromDebt(debt));
      setErrors({});
      setToast(null);
      mutation.reset?.();
      createProvision.reset?.();
    }
  }, [open, debt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC to close
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      // duration_months est dérivée des dates, du différé et de la périodicité.
      const DURATION_INPUTS = [
        'start_date', 'end_date', 'grace_period_months',
        'payment_frequency_months', 'first_payment_date',
      ];
      if (DURATION_INPUTS.includes(field)) {
        const dur = computeDurationFromDates(
          next.start_date, next.end_date, next.grace_period_months,
          next.payment_frequency_months, next.first_payment_date
        );
        next.duration_months = !isNaN(dur) ? String(dur) : '';
      }

      // Hors mensuel, le solveur de taux (mensuel par construction) ne s'applique
      // pas : on retombe sur la saisie du taux et on purge l'override.
      if (field === 'payment_frequency_months' && Number(value) !== 1) {
        next.input_mode = 'rate';
        next.monthly_payment = '';
      }

      // À la création (isEdit false), basculer is_revolving par défaut selon source_type.
      if (field === 'source_type' && !isEdit) {
        next.is_revolving = value === 'shareholder_advance';
        if (next.is_revolving) {
          next.interest_mode = 'capitalized';
        }
      }

      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  /**
   * Lit l'état courant de la ProvisionSection depuis le DOM (hidden inputs).
   * C'est l'approche choisie pour éviter de hisser l'état complet
   * de la provision dans le composant parent et de complexifier les dépendances.
   */
  function readProvisionFromDOM() {
    const panel = provisionSectionRef.current;
    if (!panel) return { enabled: false, form: null };

    const enabledInput = panel.querySelector('[data-provision-enabled]');
    const formInput    = panel.querySelector('[data-provision-form]');

    const enabled = enabledInput?.dataset?.provisionEnabled === 'true';
    let provForm = null;
    try {
      if (formInput?.dataset?.provisionForm) {
        provForm = JSON.parse(formInput.dataset.provisionForm);
      }
    } catch {
      // JSON invalide — on ignore
    }

    return { enabled, form: provForm };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validateForm(form);

    // Valide aussi la section provision si activée
    const { enabled: provEnabled, form: provForm } = readProvisionFromDOM();
    if (provEnabled && provForm) {
      const provErrs = validateProvisionForm(provForm);
      Object.assign(errs, provErrs);
    }

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    let durationMonths;
    if (form.is_revolving) {
      // Pour revolving, duration_months = nb mois entre start et in fine (min 1)
      durationMonths = Math.max(1, monthsBetween(form.start_date, form.planned_repayment_date));
    } else {
      durationMonths = parseInt(form.duration_months, 10);
    }

    const payload = {
      name:                       form.name.trim(),
      source_type:                form.source_type,
      capital_cents:              Math.round(parseFloat(form.capital) * 100),
      duration_months:            durationMonths,
      start_date:                 form.start_date,
      grace_period_months:        parseInt(form.grace_period_months || '0', 10),
      repayment_mode:             form.is_revolving ? 'in_fine' : form.repayment_mode,
      category_id:                form.category_id || null,
      counterparty_hint:          form.counterparty_hint.trim() || null,
      notes:                      form.notes.trim() || null,
      // Périodicité — le revolving n'a pas d'échéancier classique
      payment_frequency_months:   form.is_revolving ? 1 : (parseInt(form.payment_frequency_months || '1', 10) || 1),
      first_payment_date:         form.is_revolving ? null : (form.first_payment_date || null),
      funding_bank_account_id:    form.funding_bank_account_id || null,
      // Le revolving a son propre système de provision (tirages figés)
      provision_scope:            form.is_revolving ? 'none' : form.provision_scope,
      // Revolving fields
      is_revolving:               form.is_revolving,
      planned_repayment_date:     form.planned_repayment_date || null,
      interest_mode:              form.is_revolving ? form.interest_mode : 'serviced',
      provision_bank_account_id:  form.provision_bank_account_id || null,
    };

    if (form.is_revolving) {
      // Revolving : taux toujours en mode rate, pas de mensualité fixe
      payload.annual_rate_pct = parseFloat(form.annual_rate_pct) || 0;
      payload.monthly_payment_cents = null;
    } else if (form.input_mode === 'monthly_payment') {
      payload.monthly_payment_cents = Math.round(parseFloat(form.monthly_payment) * 100);
      // annual_rate_pct omis : le backend le calcule via solveAnnualRate
    } else {
      payload.annual_rate_pct = parseFloat(form.annual_rate_pct);
      payload.monthly_payment_cents = null; // repasse en mode auto si édition
    }

    if (!form.is_revolving && form.repayment_mode === 'sales_proportional') {
      payload.sales_pct = parseFloat(form.sales_pct);
      payload.monthly_ca_estimate_cents = Math.round(parseFloat(form.monthly_ca_estimate) * 100);
    }

    let debtId;
    if (isEdit) {
      await mutation.mutateAsync({ id: debt.id, ...payload });
      debtId = debt.id;
    } else {
      const created = await mutation.mutateAsync(payload);
      debtId = created?.id;
    }

    // Création de la provision si activée (uniquement à la création de dette)
    if (!isEdit && provEnabled && provForm && debtId) {
      const fundingCents = Math.round(parseFloat(provForm.funding_monthly) * 100);
      const targetAmountCents = provForm.target_amount
        ? Math.round(parseFloat(provForm.target_amount) * 100)
        : undefined;

      const provPayload = {
        name:                    provForm.name.trim(),
        category_id:             provForm.category_id,
        funding_cents_monthly:   fundingCents,
        start_date:              provForm.start_date || form.start_date,
        debt_id:                 debtId,
      };

      if (targetAmountCents && !isNaN(targetAmountCents)) {
        provPayload.target_amount_cents = targetAmountCents;
      }
      if (provForm.target_date) {
        provPayload.target_date = provForm.target_date;
      }
      if (provForm.bank_account_id) {
        provPayload.bank_account_id = provForm.bank_account_id;
      }
      if (provForm.notes?.trim()) {
        provPayload.notes = provForm.notes.trim();
      }

      try {
        await createProvision.mutateAsync(provPayload);
      } catch {
        // La dette est créée, mais la provision a échoué.
        // On affiche un toast d'avertissement et on laisse l'utilisateur
        // créer la provision manuellement depuis la page détail.
        setToast({
          type: 'warning',
          message:
            'Le prêt a été créé, mais la provision a échoué. '
            + 'Tu peux la créer manuellement depuis la page détail du prêt.',
        });
        // Ne pas fermer : l'utilisateur voit le message
        return;
      }
    }

    onClose?.();
  }

  const isSalesMode = form.repayment_mode === 'sales_proportional';
  const isDisabled  = mutation.isPending || createProvision.isPending;

  // Provision liée (mode édition)
  const linkedProvision = isEdit ? (debt?.progress?.linked_provision ?? null) : null;

  // Comptes bancaires pour le sélecteur compte dédié revolving
  const { data: bankAccountsData } = useBankAccounts();
  const bankAccountsList = Array.isArray(bankAccountsData)
    ? bankAccountsData
    : (bankAccountsData?.accounts ?? bankAccountsData?.data ?? []);

  // Preview provision pour un tirage hypothétique au capital total
  const drawdownPreview = (() => {
    if (!form.is_revolving || !form.capital || !form.start_date || !form.planned_repayment_date) return null;
    try {
      const amountCents = Math.round(parseFloat(form.capital) * 100);
      if (isNaN(amountCents) || amountCents <= 0) return null;
      return computeDrawdownProvisionClient(amountCents, form.start_date, form.planned_repayment_date);
    } catch {
      return null;
    }
  })();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/30
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Modifier la dette' : 'Nouvelle dette'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[520px] lg:rounded-none lg:max-h-none
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier la dette' : 'Nouvelle dette'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          <Field label="Nom" error={errors.name} required>
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Ex : PGE BNP 2024"
              disabled={isDisabled}
            />
          </Field>

          <Field label="Source" error={errors.source_type} required>
            <select
              className={inputClass}
              value={form.source_type}
              onChange={(e) => set('source_type', e.target.value)}
              disabled={isDisabled}
            >
              {SOURCE_TYPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>

          {/* Section enveloppe revolving */}
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.is_revolving}
                onChange={(e) => set('is_revolving', e.target.checked)}
                disabled={isDisabled}
                className="h-4 w-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                data-testid="is-revolving-toggle"
              />
              <span className="text-sm font-medium text-purple-900">
                Enveloppe renouvelable tirée progressivement
              </span>
            </label>

            {form.is_revolving && (
              <div className="space-y-3 pt-1 border-t border-purple-200">
                <p className="text-[11px] text-purple-600">
                  Apport associé ou ligne de crédit dont le tirage est progressif. Chaque tirage génère une provision mensuelle figée jusqu'à la date in fine. Les intérêts sont capitalisés et dus en bloc à la date in fine.
                </p>

                <Field
                  label="Date de remboursement in fine"
                  error={errors.planned_repayment_date}
                  required
                  hint="Capital + intérêts capitalisés remboursés en une fois à cette date."
                >
                  <input
                    type="date"
                    className={inputClass}
                    value={form.planned_repayment_date}
                    onChange={(e) => set('planned_repayment_date', e.target.value)}
                    disabled={isDisabled}
                    data-testid="planned-repayment-date"
                  />
                </Field>

                <Field label="Mode d'intérêts" error={errors.interest_mode}>
                  <select
                    className={inputClass}
                    value={form.interest_mode}
                    onChange={(e) => set('interest_mode', e.target.value)}
                    disabled={isDisabled}
                    data-testid="interest-mode-select"
                  >
                    {Object.entries(INTEREST_MODE_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Compte dédié (provision)"
                  hint="Optionnel — compte bancaire où virer les provisions mensuelles."
                >
                  <select
                    className={inputClass}
                    value={form.provision_bank_account_id}
                    onChange={(e) => set('provision_bank_account_id', e.target.value)}
                    disabled={isDisabled}
                    data-testid="provision-bank-account-select"
                  >
                    <option value="">Aucun pour l'instant</option>
                    {bankAccountsList.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name ?? acc.label ?? acc.id}
                      </option>
                    ))}
                  </select>
                </Field>

                {drawdownPreview && (
                  <p className="text-[11px] text-purple-700 bg-purple-100 rounded-lg px-3 py-2">
                    Si tu tires {formatCurrency(Math.round(parseFloat(form.capital) * 100))} aujourd'hui
                    {' → '}provision ≈ {formatCurrency(drawdownPreview.monthly_provision_cents)}/mois
                    sur {drawdownPreview.periods_remaining} mois
                    (de {drawdownPreview.provision_start} à {drawdownPreview.provision_end}).
                  </p>
                )}
              </div>
            )}
          </div>

          <Field
            label={form.is_revolving ? 'Plafond / base engagée (€)' : 'Capital emprunté (€)'}
            error={errors.capital}
            required
          >
            <input
              type="number"
              className={inputClass}
              value={form.capital}
              onChange={(e) => set('capital', e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="80000"
              disabled={isDisabled}
            />
          </Field>

          {/* Taux annuel pour revolving (affiché séparément, sans le toggle mensualité) */}
          {form.is_revolving && (
            <Field
              label="Taux annuel (%)"
              error={errors.annual_rate_pct}
              required
              hint="Taux appliqué sur la base engagée pour le calcul des intérêts capitalisés."
            >
              <input
                type="number"
                className={inputClass}
                value={form.annual_rate_pct}
                onChange={(e) => set('annual_rate_pct', e.target.value)}
                min="0"
                step="0.01"
                placeholder="6"
                disabled={isDisabled}
              />
            </Field>
          )}

          {/* Toggle mode saisie taux / mensualité — masqué en mode revolving */}
          <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3${form.is_revolving ? ' hidden' : ''}`}>
            {/* Le solveur de taux est mensuel par construction : hors mensuel,
                seule la saisie du taux a du sens (cf. CHECK debt_override_is_monthly). */}
            {Number(form.payment_frequency_months) === 1 ? (
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="input_mode"
                    value="rate"
                    checked={form.input_mode === 'rate'}
                    onChange={(e) => set('input_mode', e.target.value)}
                    disabled={isDisabled}
                    className="accent-slate-700"
                  />
                  Saisir le taux d'intérêt
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="input_mode"
                    value="monthly_payment"
                    checked={form.input_mode === 'monthly_payment'}
                    onChange={(e) => set('input_mode', e.target.value)}
                    disabled={isDisabled}
                    className="accent-slate-700"
                  />
                  Saisir la mensualité réelle
                </label>
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                Saisie de la mensualité réelle indisponible en périodicité
                {' '}{(PAYMENT_FREQUENCY_LABELS[Number(form.payment_frequency_months)] ?? '').toLowerCase()} — renseigner le taux annuel.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              {/* Champ taux */}
              <Field
                label="Taux annuel (%)"
                error={errors.annual_rate_pct}
                required={form.input_mode === 'rate'}
                hint={form.input_mode === 'monthly_payment' ? 'Calculé automatiquement' : undefined}
              >
                {form.input_mode === 'rate' ? (
                  <input
                    type="number"
                    className={inputClass}
                    value={form.annual_rate_pct}
                    onChange={(e) => set('annual_rate_pct', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="1.50"
                    disabled={isDisabled}
                  />
                ) : (
                  <div className={`${inputClass} bg-slate-100 text-slate-500 cursor-default`}>
                    {(() => {
                      const capital_cents = Math.round(parseFloat(form.capital) * 100);
                      const mp_cents = Math.round(parseFloat(form.monthly_payment) * 100);
                      const n = parseInt(form.duration_months, 10);
                      const g = parseInt(form.grace_period_months || '0', 10) || 0;
                      const rate = solveAnnualRateClient(capital_cents, mp_cents, n, g);
                      if (rate === null) return '—';
                      if (rate === 0) return '0,00 %';
                      return `${String(rate).replace('.', ',')} %`;
                    })()}
                  </div>
                )}
              </Field>

              {/* Champ mensualité */}
              <Field
                label="Mensualité réelle (€)"
                error={errors.monthly_payment}
                required={form.input_mode === 'monthly_payment'}
                hint={form.input_mode === 'rate' ? 'Calculée automatiquement' : undefined}
              >
                {form.input_mode === 'monthly_payment' ? (
                  <input
                    type="number"
                    className={inputClass}
                    value={form.monthly_payment}
                    onChange={(e) => set('monthly_payment', e.target.value)}
                    min="0.01"
                    step="0.01"
                    placeholder="920.00"
                    disabled={isDisabled}
                  />
                ) : (
                  <div className={`${inputClass} bg-slate-100 text-slate-500 cursor-default`}>
                    {(() => {
                      const capital_cents = Math.round(parseFloat(form.capital) * 100);
                      const rate = parseFloat(form.annual_rate_pct);
                      const n = parseInt(form.duration_months, 10);
                      const g = parseInt(form.grace_period_months || '0', 10) || 0;
                      const cents = computeMonthlyPaymentCents(capital_cents, rate, n, g);
                      if (!cents) return '—';
                      return `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
                    })()}
                  </div>
                )}
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date de début" error={errors.start_date} required>
              <input
                type="date"
                className={inputClass}
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
                disabled={isDisabled}
              />
            </Field>

            {!form.is_revolving && (
              <Field
                label="Date de fin"
                error={errors.end_date}
                required
                hint="Date du dernier prélèvement"
              >
                <input
                  type="date"
                  className={inputClass}
                  value={form.end_date}
                  onChange={(e) => set('end_date', e.target.value)}
                  disabled={isDisabled}
                />
              </Field>
            )}
          </div>

          {!form.is_revolving && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Périodicité des échéances"
                error={errors.payment_frequency_months}
                hint="Fréquence de prélèvement des intérêts et du capital"
              >
                <select
                  className={inputClass}
                  value={form.payment_frequency_months}
                  onChange={(e) => set('payment_frequency_months', e.target.value)}
                  disabled={isDisabled}
                >
                  {Object.entries(PAYMENT_FREQUENCY_LABELS).map(([months, label]) => (
                    <option key={months} value={months}>{label}</option>
                  ))}
                </select>
              </Field>

              <Field
                label="Première échéance"
                error={errors.first_payment_date}
                hint="Vide = première échéance à la date de début. À renseigner si le premier prélèvement est décalé."
              >
                <input
                  type="date"
                  className={inputClass}
                  value={form.first_payment_date}
                  onChange={(e) => set('first_payment_date', e.target.value)}
                  min={form.start_date || undefined}
                  disabled={isDisabled}
                />
              </Field>
            </div>
          )}

          {!form.is_revolving && Number(form.payment_frequency_months) !== 1 && form.duration_months && (
            <p className="text-xs text-slate-500 -mt-1">
              {Math.floor(Number(form.duration_months) / Number(form.payment_frequency_months))} échéances
              {' '}sur {form.duration_months} mois
              {form.first_payment_date && <> — première le {form.first_payment_date.split('-').reverse().join('/')}</>}
            </p>
          )}

          {!form.is_revolving && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              <p className="text-xs font-medium text-slate-600">Comptes bancaires</p>

              <Field
                label="Compte de réception des fonds"
                hint="Optionnel — compte où le prêt a été encaissé."
              >
                <select
                  className={inputClass}
                  value={form.funding_bank_account_id}
                  onChange={(e) => set('funding_bank_account_id', e.target.value)}
                  disabled={isDisabled}
                  data-testid="funding-bank-account-select"
                >
                  <option value="">Aucun</option>
                  {bankAccountsList.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name ?? acc.label ?? acc.id}</option>
                  ))}
                </select>
              </Field>

              <Field
                label="Provisionner le remboursement"
                error={errors.provision_scope}
                hint="Constitue progressivement la somme due sur un compte dédié, et suit l'écart avec le solde réel."
              >
                <select
                  className={inputClass}
                  value={form.provision_scope}
                  onChange={(e) => set('provision_scope', e.target.value)}
                  disabled={isDisabled}
                  data-testid="provision-scope-select"
                >
                  <option value="none">Non</option>
                  <option value="principal">Oui — nominal seul</option>
                  <option value="principal_and_interest">Oui — nominal + intérêts</option>
                </select>
              </Field>

              {form.provision_scope !== 'none' && (
                <Field
                  label="Compte dédié à la provision"
                  error={errors.provision_bank_account_id}
                  required
                  hint="Le suivi compare le provisionné théorique au solde réel de ce compte."
                >
                  <select
                    className={inputClass}
                    value={form.provision_bank_account_id}
                    onChange={(e) => set('provision_bank_account_id', e.target.value)}
                    disabled={isDisabled}
                    data-testid="classic-provision-bank-account-select"
                  >
                    <option value="">Sélectionner un compte</option>
                    {bankAccountsList.map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.name ?? acc.label ?? acc.id}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
          )}

          {!form.is_revolving && (
            <Field
              label="Différé (mois sans remboursement)"
              error={errors.grace_period_months}
              hint="0 si pas de différé. Période avant la première échéance."
            >
              <input
                type="number"
                className={inputClass}
                value={form.grace_period_months}
                onChange={(e) => set('grace_period_months', e.target.value)}
                min="0"
                step="1"
                placeholder="0"
                disabled={isDisabled}
              />
            </Field>
          )}

          {!form.is_revolving && form.duration_months && parseInt(form.duration_months, 10) > 0 && (
            <p className="text-xs text-slate-500 -mt-2 italic">
              Soit <strong>{form.duration_months}</strong> mensualité{parseInt(form.duration_months, 10) > 1 ? 's' : ''}
              {parseInt(form.grace_period_months || '0', 10) > 0 && (
                <> (après {form.grace_period_months} mois de différé)</>
              )}.
            </p>
          )}

          {!form.is_revolving && (
            <Field label="Mode de remboursement" error={errors.repayment_mode} required>
              <select
                className={inputClass}
                value={form.repayment_mode}
                onChange={(e) => set('repayment_mode', e.target.value)}
                disabled={isDisabled}
              >
                {REPAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>
          )}

          {isSalesMode && (
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 space-y-3">
              <p className="text-xs font-medium text-sky-700">Paramètres proportionnels au CA</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="% du CA" error={errors.sales_pct} required>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.sales_pct}
                    onChange={(e) => set('sales_pct', e.target.value)}
                    min="0.01"
                    max="100"
                    step="0.01"
                    placeholder="5.00"
                    disabled={isDisabled}
                  />
                </Field>

                <Field label="CA mensuel estimé (€)" error={errors.monthly_ca_estimate} required>
                  <input
                    type="number"
                    className={inputClass}
                    value={form.monthly_ca_estimate}
                    onChange={(e) => set('monthly_ca_estimate', e.target.value)}
                    min="0.01"
                    step="0.01"
                    placeholder="20000"
                    disabled={isDisabled}
                  />
                </Field>
              </div>
            </div>
          )}

          <Field label="Catégorie">
            <CategoryCombobox
              value={form.category_id}
              onChange={(v) => set('category_id', v ?? '')}
              placeholder="Catégorie de remboursement…"
            />
          </Field>

          <Field label="Intitulé fournisseur (suggestions)">
            <input
              type="text"
              className={inputClass}
              value={form.counterparty_hint}
              onChange={(e) => set('counterparty_hint', e.target.value)}
              placeholder="Qonto - SAS Carqueiranne"
              disabled={isDisabled}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Les suggestions de transactions seront filtrées sur cet intitulé (ILIKE partiel sur label / fournisseur).
            </p>
          </Field>

          <Field label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Informations complémentaires…"
              disabled={isDisabled}
            />
          </Field>

          {/* Section provision — uniquement à la création, ou encart lecture seule en édition */}
          <div ref={provisionSectionRef}>
            <ProvisionSection
              debtForm={form}
              errors={errors}
              isDisabled={isDisabled}
              linkedProvision={linkedProvision}
            />
          </div>

          {(mutation.error || createProvision.error) && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {mutation.error?.message ?? createProvision.error?.message}
            </p>
          )}

          {/* Erreurs provision inline */}
          {(errors.prov_name || errors.prov_category_id || errors.prov_funding || errors.prov_start_date) && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              La section provision contient des erreurs.
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-safe">
            <Button
              type="submit"
              loading={mutation.isPending || createProvision.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {isEdit ? 'Enregistrer' : 'Créer la dette'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isDisabled}
            >
              Annuler
            </Button>
          </div>
        </form>
      </div>

      {/* Toast provision warning */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
